// Read themeData at call time so values are always fresh
const td           = () => window.themeData ?? {};
const nonce        = () => td().nonce ?? '';
const wcStoreNonce = () => td().wcStoreNonce ?? '';

// ── URL builders ──────────────────────────────────────────────────────────────

function anviUrl(endpoint, params = {}) {
  const base   = (td().apiUrl ?? '').replace(/\/$/, '');
  const qs     = new URLSearchParams(params).toString();
  const joiner = base.includes('?') ? '&' : '?';
  return `${base}/little-anvi/v1${endpoint}${qs ? joiner + qs : ''}`;
}

function storeUrl(endpoint, params = {}) {
  const base   = (td().apiUrl ?? '').replace(/\/$/, '');
  const qs     = new URLSearchParams(params).toString();
  const joiner = base.includes('?') ? '&' : '?';
  return `${base}/wc/store/v1${endpoint}${qs ? joiner + qs : ''}`;
}

// ── Products & categories — uses custom public endpoints (no auth needed) ─────

export async function fetchProducts(params = {}) {
  const res = await fetch(anviUrl('/products', { per_page: 8, ...params }));
  if (!res.ok) throw new Error(`fetchProducts: HTTP ${res.status}`);
  return res.json();
}

// Paginated variant — returns { products, total, totalPages }
export async function fetchProductsPage(params = {}) {
  const res = await fetch(anviUrl('/products', { per_page: 9, ...params }));
  if (!res.ok) throw new Error(`fetchProductsPage: HTTP ${res.status}`);
  const products   = await res.json();
  const total      = parseInt(res.headers.get('X-WP-Total')      ?? '0',  10);
  const totalPages = parseInt(res.headers.get('X-WP-TotalPages') ?? '1',  10);
  return { products, total, totalPages };
}

export async function fetchPriceRange() {
  const res = await fetch(anviUrl('/products/price-range'));
  if (!res.ok) return { min: 0, max: 500 };
  return res.json();
}

export async function fetchCategories(params = {}) {
  const { exclude, ...rest } = params;
  const res = await fetch(anviUrl('/product-categories', { per_page: 20, ...rest }));
  if (!res.ok) throw new Error(`fetchCategories: HTTP ${res.status}`);
  return res.json();
}

export async function fetchProductById(id) {
  const res = await fetch(anviUrl(`/products/${id}`));
  if (!res.ok) throw new Error(`fetchProductById: HTTP ${res.status}`);
  return res.json();
}

// ── Cart — WC Store API (public, cookie-session based) ────────────────────────

export async function addToCart(productId, quantity = 1) {
  const res = await fetch(storeUrl('/cart/add-item'), {
    method:  'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'Nonce': wcStoreNonce() },
    body:    JSON.stringify({ id: productId, quantity }),
  });
  if (!res.ok) throw new Error(`addToCart: HTTP ${res.status}`);
  const data = await res.json();
  window.dispatchEvent(new CustomEvent('anvi:cart-update', { detail: { count: data.items_count ?? 0 } }));
  return data;
}

export async function fetchCart() {
  const res = await fetch(storeUrl('/cart'), {
    credentials: 'same-origin',
    headers: { 'Nonce': wcStoreNonce() },
  });
  if (!res.ok) throw new Error(`fetchCart: HTTP ${res.status}`);
  return res.json();
}

// ── Theme helpers ─────────────────────────────────────────────────────────────

export async function subscribeNewsletter(email) {
  const res = await fetch(anviUrl('/newsletter'), {
    method:  'POST',
    headers: { 'Content-Type': 'application/json', 'X-WP-Nonce': nonce() },
    body:    JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error(`subscribeNewsletter: HTTP ${res.status}`);
  return res.json();
}

export function formatPrice(amount, currency) {
  const c = currency ?? td().currency ?? '$';
  return `${c}${parseFloat(amount ?? 0).toFixed(2)}`;
}
